// Permission modes. The approve/deny "ask before acting" gate was removed from
// the product on 2026-06-23 (owner call) — the agent always runs tools without
// a confirmation card. That collapsed the old four modes (the only difference
// between default / acceptEdits / auto was *whether* connectors gated, which no
// longer happens) down to two meaningful states:
//   - "default" — run normally.
//   - "plan"    — prepend a read-only directive so the agent drafts a plan first.
// The server still accepts the legacy "acceptEdits" / "auto" values (and a
// persisted one resolves harmlessly to "default" below), so re-introducing the
// gate later is a frontend-only change here plus the one-liner in agent.py.
export type PermissionMode = "default" | "plan";

export const PERMISSION_MODE_ORDER: PermissionMode[] = ["default", "plan"];

export const PERMISSION_MODE_LABEL: Record<PermissionMode, string> = {
  default: "auto-run",
  plan: "plan mode",
};

const PERMISSION_MODE_LS_PREFIX = "railyard_permission_mode:";

export function readPermissionMode(taskId: string | null): PermissionMode {
  if (!taskId || typeof window === "undefined") return "default";
  const v = localStorage.getItem(PERMISSION_MODE_LS_PREFIX + taskId);
  return PERMISSION_MODE_ORDER.includes(v as PermissionMode)
    ? (v as PermissionMode)
    : "default";
}

export function writePermissionMode(taskId: string, mode: PermissionMode): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(PERMISSION_MODE_LS_PREFIX + taskId, mode);
}

// ---- Effort selection (ENG-4640) ----
//
// The composer used to pick Haiku / Sonnet / Opus. Every new task now runs
// DEFAULT_MODEL (Opus 5.5 since ENG-5474); the user-facing lever is effort.
// Opus 5.5 supports the full Anthropic ladder (low / medium / high / xhigh /
// max); High is the default (ENG-4647 — parked back on High until we have
// more evals; Medium remains a picker option). Persisted per-task. The old
// `railyard_model:` keys migrate in readEffort: haiku→low, sonnet→medium,
// opus/fable→high.

export type EffortChoice = "low" | "medium" | "high" | "xhigh" | "max";

export const VISIBLE_EFFORT_ORDER: EffortChoice[] = [
  "low",
  "medium",
  "high",
  "xhigh",
  "max",
];
export const DEFAULT_EFFORT: EffortChoice = "high";

export const EFFORT_LABELS: Record<EffortChoice, string> = {
  low: "Low",
  medium: "Medium",
  high: "High",
  // Anthropic's wire name is `xhigh`; the chip and menu say Extra High.
  xhigh: "Extra High",
  max: "Max",
};

// Visible composer chip names the dimension, not just the level (ENG-5194).
// Menu rows keep the bare EFFORT_LABELS names with EFFORT_HINTS as subtitles.
export function effortChipLabel(effort: EffortChoice): string {
  return `Effort: ${EFFORT_LABELS[effort]}`;
}

/** Chip text: `Effort: High`, or the model name when a legacy pin is locked. */
export function effortChipDisplay(
  effort: EffortChoice,
  legacyModelId?: string | null,
): string {
  if (legacyModelId) return modelDisplayShortLabelFromId(legacyModelId);
  return effortChipLabel(effort);
}

/** Read-only task "..." menu line once effort is settled (ENG-5863). */
export function settledEffortLabel(
  effort: EffortChoice,
  legacyModelId?: string | null,
): string {
  const label = effortChipDisplay(effort, legacyModelId);
  return legacyModelId ? `Model: ${label}` : label;
}

// Speed-vs-thoroughness only — no latency numbers or credit costs (ENG-5194).
// One-line subtitles: wrapping "Balanced speed and thoroughness" made the
// menu look like a wall of captions next to the chip.
export const EFFORT_HINTS: Record<EffortChoice, string> = {
  low: "Fastest",
  medium: "Faster",
  high: "Balanced",
  xhigh: "More thorough",
  max: "Most thorough",
};

/** Plus-menu attach label (ENG-5194). Stays in the overflow — a second
 * footer chip next to the plus duplicated the first row. */
export const ADD_FILES_LABEL = "Add files";

const EFFORT_LS_PREFIX = "railyard_effort:";

export function isEffortChoice(
  value: string | null | undefined,
): value is EffortChoice {
  return (
    value === "low" ||
    value === "medium" ||
    value === "high" ||
    value === "xhigh" ||
    value === "max"
  );
}

/** Map a persisted/server effort (or a legacy model-tier key) to a choice. */
export function effortFromValue(value: string | null | undefined): EffortChoice {
  if (value === "haiku") return "low";
  if (value === "sonnet") return "medium";
  if (value === "opus" || value === "fable") return "high";
  if (isEffortChoice(value)) return value;
  return DEFAULT_EFFORT;
}

export function readEffort(taskId: string | null): EffortChoice {
  if (!taskId || typeof window === "undefined") return DEFAULT_EFFORT;
  const v = localStorage.getItem(EFFORT_LS_PREFIX + taskId);
  if (isEffortChoice(v)) return v;
  // Migrate a leftover model-switcher preference from before ENG-4640.
  return effortFromValue(localStorage.getItem(MODEL_LS_PREFIX + taskId));
}

export function writeEffort(taskId: string, effort: EffortChoice): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(EFFORT_LS_PREFIX + taskId, effort);
}

// ---- Model selection (legacy) ----
//
// Kept so a task pinned to a non-default model (Sonnet 4.6, Haiku, …) still
// renders that model's name on the locked chip. New tasks do not pick a
// model — see EffortChoice above.

export type ModelChoice = "haiku" | "sonnet" | "opus" | "fable";

const MODEL_LS_PREFIX = "railyard_model:";

// Models shown in the composer picker. Fable is omitted — not because it is
// unavailable (ENG-4069 unblocked it on the production key) but because it is
// not the platform default and the composer no longer picks models at all
// (ENG-4640). A task can still be pinned to Fable through the API, which is
// why the label maps below have to render it.
export const VISIBLE_MODEL_ORDER: ModelChoice[] = ["haiku", "sonnet", "opus"];

// Full, brand-forward model name — primary label in the picker + tooltips.
export const MODEL_LABELS: Record<ModelChoice, string> = {
  haiku: "Claude Haiku 4.5",
  sonnet: "Claude Sonnet 5",
  opus: "Claude Opus 5.5",
  fable: "Claude Fable 5",
};

// Compact model name for the inline chip (the "Claude" prefix is implied).
export const MODEL_SHORT_LABELS: Record<ModelChoice, string> = {
  haiku: "Haiku 4.5",
  sonnet: "Sonnet 5",
  opus: "Opus 5.5",
  fable: "Fable 5",
};

// One-word speed/capability hint shown as the picker subtitle, so the ladder
// still reads low → high even though the primary label is now the model name.
export const MODEL_HINT_LABEL: Record<ModelChoice, string> = {
  haiku: "Fastest",
  sonnet: "Balanced",
  opus: "Deep reasoning",
  fable: "Most advanced",
};

// Wire ids sent to the API. Must stay in sync with KNOWN_MODELS in
// src/railyard/ma/_constants.py. "opus" MUST equal the server's DEFAULT_MODEL,
// so callers may send `undefined` for it to mean "use default" — and it moves
// in the same change as DEFAULT_MODEL (ENG-5474). A stale id here is read as a
// user PIN (`legacyPinnedModelId`) and re-sent as a model override, which MA
// treats as a full replacement of the agent's model object: the old model at
// ITS default effort, undoing the upgrade and the user's effort choice alike
// (ENG-3564, ENG-5317).
export const MODEL_IDS: Record<ModelChoice, string> = {
  haiku: "claude-haiku-4-5",
  sonnet: "claude-sonnet-5",
  opus: "claude-opus-5-5",
  fable: "claude-fable-5",
};

/** Non-default model a locked task is actually running.
 *
 * Prefer the explicit `model` pin (a real user/routine choice). Fall back to
 * `effective_model` only once the conversation has started, so a pre-stamp
 * Sonnet thread still labels the chip correctly. Do **not** use
 * `effective_model` on a blank unused prewarm — that value is the old
 * platform default the server will discard
 * (`_stale_default_model_session`); treating it as a pin would lock the
 * chip and resend the obsolete id on first send.
 *
 * Nor when the server says that value is DERIVED (ENG-5317): with
 * effort->model routing on, an xhigh/max task is legitimately not on the
 * default, and reading that as a pin would hide the effort switcher AND
 * resend the routed id as a model override — which MA treats as a full
 * replacement of the agent's model object, wiping the clone's baked effort
 * back to High (ENG-3564). Only the server knows the map, so it answers;
 * an older payload omits the flag and keeps the previous behaviour.
 */
export function legacyPinnedModelId(task: {
  model?: string | null;
  effective_model?: string | null;
  effective_model_is_derived?: boolean | null;
  turns?: unknown[] | null;
} | null | undefined): string | null {
  if (task?.model && task.model !== MODEL_IDS.opus) return task.model;
  const started = (task?.turns?.length ?? 0) > 0;
  if (
    started &&
    task?.effective_model &&
    task.effective_model !== MODEL_IDS.opus &&
    task.effective_model_is_derived !== true
  ) {
    return task.effective_model;
  }
  return null;
}

// Retired ids that tasks can still be pinned to (a task created before a
// default bump keeps its original model — see `modelOverride` in tasks.tsx).
// Without these, such a task renders under the CURRENT tier label and claims to
// be running a model it isn't. Keyed by exact wire id; short label is the full
// label minus the "Claude " prefix.
//
// Both current-generation Opus ids get EXACT entries: `modelChoiceFromId`
// prefix-matches every `claude-opus*` id to the single "opus" tier, so without
// them a 5.5 task and a 5 task would both render under whichever label that
// tier happens to carry.
const LEGACY_MODEL_LABELS: Record<string, string> = {
  "claude-opus-5-5": "Claude Opus 5.5",
  // The default until ENG-5474. Tasks pinned to it, and sessions built on it,
  // keep running it.
  "claude-opus-5": "Claude Opus 5",
  "claude-opus-4-8": "Claude Opus 4.8",
  "claude-opus-4-7": "Claude Opus 4.7",
  "claude-sonnet-4-6": "Claude Sonnet 4.6",
  // Not retired — a live override (ENG-4069). It needs an exact-id entry
  // because the "fable" tier label is "Claude Fable 5", and prefix matching
  // alone would render a 5.1-pinned task as 5.
  "claude-fable-5-1": "Claude Fable 5.1",
};

/** Full display label for a wire model id (handles legacy pinned ids). */
export function modelDisplayLabelFromId(id: string | null | undefined): string {
  const legacy = id ? LEGACY_MODEL_LABELS[id] : undefined;
  if (legacy) return legacy;
  return MODEL_LABELS[modelChoiceFromId(id)];
}

/** Compact display label for a wire model id (handles legacy pinned ids). */
export function modelDisplayShortLabelFromId(id: string | null | undefined): string {
  const legacy = id ? LEGACY_MODEL_LABELS[id] : undefined;
  if (legacy) return legacy.replace(/^Claude /, "");
  return MODEL_SHORT_LABELS[modelChoiceFromId(id)];
}

// Map a persisted/server model id back to its tier. Prefix-matched so dated
// suffixes (e.g. claude-haiku-4-5-20251001) still resolve.
export function modelChoiceFromId(id: string | null | undefined): ModelChoice {
  if (id?.startsWith("claude-fable")) return "fable";
  if (id?.startsWith("claude-opus")) return "opus";
  if (id?.startsWith("claude-haiku")) return "haiku";
  if (id?.startsWith("claude-sonnet")) return "sonnet";
  return "opus";
}

/** Map a tier to a selectable model (drops hidden tiers like Fable). */
export function normalizeModelChoice(m: ModelChoice): ModelChoice {
  return VISIBLE_MODEL_ORDER.includes(m) ? m : "opus";
}

export function readModel(taskId: string | null): ModelChoice {
  if (!taskId || typeof window === "undefined") return "opus";
  const v = localStorage.getItem(MODEL_LS_PREFIX + taskId);
  const raw =
    v === "sonnet" || v === "haiku" || v === "opus" || v === "fable" ? v : "opus";
  return normalizeModelChoice(raw as ModelChoice);
}

export function writeModel(taskId: string, model: ModelChoice): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(MODEL_LS_PREFIX + taskId, model);
}

// Web search is always attached to the agent toolset server-side; the toggle
// is a client-side hint. When OFF we prepend a directive to the user's turn
// so the agent answers from memory + sources only. Default ON, persisted
// per-browser (the toggle is a workspace preference, not per-task).
const WEB_SEARCH_LS_KEY = "railyard_web_search_enabled";
export const NO_WEB_SEARCH_DIRECTIVE =
  "[Don't use web_search or web_fetch this turn — answer from memory, attached files, and project sources only.]\n\n";

export function readWebSearchEnabled(): boolean {
  if (typeof window === "undefined") return true;
  const v = localStorage.getItem(WEB_SEARCH_LS_KEY);
  return v === null ? true : v === "1";
}

export function writeWebSearchEnabled(enabled: boolean): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(WEB_SEARCH_LS_KEY, enabled ? "1" : "0");
}

// ---- Outcome (opt-in MA define_outcome grader loop) ----
//
// An Outcome attaches a definition-of-done + rubric to a turn: the agent
// produces a deliverable, an independent grader scores it against the rubric,
// and the session iterates up to `maxIterations`. Opt-in and OFF by default —
// every iteration is a full revision + grade pass, so it is real money on a
// multi-specialist run and must never be the global default (see
// docs/outcomes-and-budgets-proposal.md). Persisted per-task so the rubric
// survives a reload; re-entering it each turn would be punishing.

export type OutcomeDraft = {
  enabled: boolean;
  description: string;
  // Inline text rubric ({type:"text"}). Ignored when `graderName` is set —
  // a saved grader supplies a file rubric ({type:"file"}) instead.
  rubric: string;
  // Name of a saved grader to use instead of an inline rubric, or null.
  graderName: string | null;
  maxIterations: number;
};

export const DEFAULT_OUTCOME_DRAFT: OutcomeDraft = {
  enabled: false,
  description: "",
  rubric: "",
  graderName: null,
  maxIterations: 3,
};

// MA caps max_iterations at 20; floor of 1 (a single graded pass).
export const OUTCOME_MAX_ITERATIONS = 20;
export const OUTCOME_MIN_ITERATIONS = 1;

const OUTCOME_LS_PREFIX = "railyard_outcome:";

export function readOutcomeDraft(taskId: string | null): OutcomeDraft {
  if (!taskId || typeof window === "undefined") return { ...DEFAULT_OUTCOME_DRAFT };
  try {
    const raw = localStorage.getItem(OUTCOME_LS_PREFIX + taskId);
    if (!raw) return { ...DEFAULT_OUTCOME_DRAFT };
    const v = JSON.parse(raw) as Partial<OutcomeDraft>;
    const iters =
      typeof v.maxIterations === "number" ? Math.round(v.maxIterations) : 3;
    return {
      enabled: !!v.enabled,
      description: typeof v.description === "string" ? v.description : "",
      rubric: typeof v.rubric === "string" ? v.rubric : "",
      graderName: typeof v.graderName === "string" ? v.graderName : null,
      maxIterations: Math.min(
        OUTCOME_MAX_ITERATIONS,
        Math.max(OUTCOME_MIN_ITERATIONS, iters),
      ),
    };
  } catch {
    return { ...DEFAULT_OUTCOME_DRAFT };
  }
}

export function writeOutcomeDraft(taskId: string, draft: OutcomeDraft): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(OUTCOME_LS_PREFIX + taskId, JSON.stringify(draft));
}

// ---- Verification mode (curated anti-hallucination outcome) ----
//
// "Verification" is a
// one-click entry point to the factual-faithfulness outcome grader (the
// `verification` grader / skills/_engine/evals/grader-verification.md): the agent
// produces the deliverable, an INDEPENDENT grader verifies that every
// load-bearing claim ties out to a source (or is flagged as an assumption),
// and the session revises until it passes. This is the differentiator vs the
// default — the default cites as it writes; Verification independently checks
// and corrects. Unlike the full OutcomeControl — gated behind the
// (off-by-default) `outcomes` feature flag — this toggle is always available.
// Lives in the composer "+" menu and shows a pill on the prompt box when on.
//
// Resolution is per-task and tri-state: an explicit per-task choice (the
// composer toggle) wins; absent one, the task follows its project's
// `verification_default`; absent that, OFF. Storing per-task (not per-browser)
// lets the project default seed each task while still letting a user flip one
// task without changing the project or every other task.
//
// When a workspace has published the `verification` grader the request uses its
// uploaded file rubric; otherwise it falls back to the compact inline rubric
// below, so the toggle works with no admin setup. The inline text is a SUMMARY
// of grader-verification.md (the canonical, fuller rubric) — keep them in sync.
const VERIFICATION_LS_PREFIX = "railyard_verification:";
const VERIFICATION_MODE_LS_PREFIX = "railyard_verification_mode:";

export type VerificationMode = "check_once" | "fix_until_clear";
export const DEFAULT_VERIFICATION_MODE: VerificationMode = "check_once";
export const VERIFICATION_MODE_LABEL: Record<VerificationMode, string> = {
  check_once: "Check once",
  fix_until_clear: "Fix until clear",
};

// Visible supporting line under the Verification menu item (ENG-5194).
// The item itself stays named "Verification"; this is the plain-language gloss.
export const VERIFICATION_MENU_HINT = "Check claims against sources.";

// Nested radio copy under Verification when it's on. Kept short so the plus
// menu doesn't grow a wrapping paragraph above the choices (ENG-5194).
export const VERIFICATION_MODE_HINT: Record<VerificationMode, string> = {
  check_once: "Verify once. Fixes need your approval.",
  fix_until_clear:
    "Fix and recheck up to 3 rounds; may create new versions.",
};

// Name of the saved grader the toggle prefers when an admin has published it
// (matches the slug of skills/_engine/evals/grader-verification.md).
export const VERIFICATION_GRADER_NAME = "verification";

// Definition-of-done handed to the outcome loop alongside the rubric.
export const VERIFICATION_DESCRIPTION =
  "Deliver the requested artifact with every load-bearing claim verified " +
  "against its source: each number, named fact, quote, and causal/comparative " +
  "statement must tie out to a source in the session (document, project " +
  "memory, tool result, or a sourced derivation) or be explicitly labeled an " +
  "assumption. No fabricated or unsupported facts.";

// "Check once" grades a single pass; "Fix until clear" may iterate up to this cap.
export const VERIFICATION_CHECK_ONCE_ITERATIONS = 1;
export const VERIFICATION_FIX_MAX_ITERATIONS = 3;
// Legacy alias — outcome drafts and graders still reference a default cap.
export const VERIFICATION_MAX_ITERATIONS = VERIFICATION_FIX_MAX_ITERATIONS;

// Compact inline rubric (fallback when no published `verification` grader exists).
// Summary of grader-verification.md; the bias is deliberate: penalize UNSUPPORTED
// claims, not all claims — grounded reasoning/inference from sourced facts
// passes. The bias was calibrated against claude-opus-4-8's documented
// over-abstention so the gate wouldn't push it into refusing answerable
// questions; it has NOT been re-validated on claude-opus-5 (ENG-3273), so read
// the caveat as provisional rather than as a claim about the current default.
export const VERIFICATION_RUBRIC = [
  "Factual faithfulness gate. Walk every load-bearing claim (numbers,",
  "percentages, dates, named facts, quotes, causal and comparative",
  "statements). The deliverable PASSES only when all of the following hold:",
  "",
  "- No fabricated or unsupported claim: every specific fact traces to a",
  "  source in this session (a document, project memory, a tool result, or a",
  "  stated derivation whose inputs are themselves sourced).",
  "- No misattributed or overstated claim: where a source exists it supports",
  "  the claim as stated (right figure, right period; a 'may' not turned into",
  "  a 'will'; a single data point not generalized to a trend).",
  "- Numbers tie: totals foot, percentages reconcile to inputs, bridges sum",
  "  to their endpoints. A sourced figure that doesn't tie FAILS.",
  "- Every assumption or estimate is labeled at the point of use, not hidden",
  "  in a footnote.",
  "- No undisclosed unverifiable claim in a load-bearing position: ground it,",
  "  cut it, or label it 'unconfirmed from available sources.'",
  "",
  "Reward declining over fabricating: 'the sources don't specify X' is a",
  "correct, complete answer, never a gap to mark down. Do NOT penalize",
  "reasoning or logically-determinable inferences from sourced",
  "facts — only unsupported specifics. Faithfulness is an independent hard",
  "gate; do not trade a fabrication away against strong craft elsewhere.",
].join("\n");

// The wire OutcomeRequest the Verification toggle attaches. Prefers a published
// `verification` grader (uploaded file rubric); falls back to the compact inline
// rubric so it works with no admin setup. Shared by the task composer and the
// project-home composer so both attach the SAME outcome on the first turn.
export type VerificationGrader = {
  name: string;
  file_id?: string | null;
  max_iterations?: number | null;
};

export function buildVerificationOutcome(
  graders: VerificationGrader[],
  mode: VerificationMode = DEFAULT_VERIFICATION_MODE,
): {
  description: string;
  rubric:
    | { type: "file"; file_id: string }
    | { type: "text"; content: string };
  max_iterations: number;
} {
  const g = graders.find((x) => x.name === VERIFICATION_GRADER_NAME);
  const maxIterations =
    mode === "fix_until_clear"
      ? VERIFICATION_FIX_MAX_ITERATIONS
      : VERIFICATION_CHECK_ONCE_ITERATIONS;
  if (g?.file_id) {
    return {
      description: VERIFICATION_DESCRIPTION,
      rubric: { type: "file", file_id: g.file_id },
      max_iterations: maxIterations,
    };
  }
  return {
    description: VERIFICATION_DESCRIPTION,
    rubric: { type: "text", content: VERIFICATION_RUBRIC },
    max_iterations: maxIterations,
  };
}

// null = no explicit per-task choice → caller falls back to the project
// default. Keyed per task so the project default can seed each one.
export function readVerificationOverride(taskId: string | null): boolean | null {
  if (!taskId || typeof window === "undefined") return null;
  const v = localStorage.getItem(VERIFICATION_LS_PREFIX + taskId);
  return v === null ? null : v === "1";
}

export function writeVerificationOverride(taskId: string, enabled: boolean): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(VERIFICATION_LS_PREFIX + taskId, enabled ? "1" : "0");
}

export function readVerificationMode(taskId: string | null): VerificationMode {
  if (!taskId || typeof window === "undefined") return DEFAULT_VERIFICATION_MODE;
  const v = localStorage.getItem(VERIFICATION_MODE_LS_PREFIX + taskId);
  return v === "fix_until_clear" ? "fix_until_clear" : DEFAULT_VERIFICATION_MODE;
}

export function writeVerificationMode(taskId: string, mode: VerificationMode): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(VERIFICATION_MODE_LS_PREFIX + taskId, mode);
}

const VERIFICATION_ACTIONS_DISMISSED_LS_PREFIX =
  "railyard_verification_actions_dismissed:";

/** Hide the post-verification action card after the user picks keep/finish. */
export function readVerificationActionsDismissed(turnKey: string): boolean {
  if (typeof window === "undefined") return false;
  return localStorage.getItem(VERIFICATION_ACTIONS_DISMISSED_LS_PREFIX + turnKey) === "1";
}

export function writeVerificationActionsDismissed(turnKey: string): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(VERIFICATION_ACTIONS_DISMISSED_LS_PREFIX + turnKey, "1");
}

// Whether the task right rail (Progress · Working folder · Context) is shown.
// Per-browser, default ON. Reading localStorage during SSR would render the
// wrong state, so callers should hydrate this after mount.
const RAIL_VISIBLE_LS_KEY = "railyard_task_rail_visible";

export function readRailVisible(): boolean {
  if (typeof window === "undefined") return true;
  return localStorage.getItem(RAIL_VISIBLE_LS_KEY) !== "0";
}

export function writeRailVisible(visible: boolean): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(RAIL_VISIBLE_LS_KEY, visible ? "1" : "0");
}
